import React, { useMemo, useState } from 'react';
import ReactECharts from 'echarts-for-react';
import { buildBucketTooltipHtml, buildEventBuckets, EVENT_TOOLTIP_FONT_FAMILY, formatInsightDate } from './chartEventHelpers';
import { buildAverageLine, buildLinearTrendLine } from './trendLine.helpers';
import { displayModeEnums, getGoalMasteryLine } from '../constants';

const FONT_FAMILY = '"GT Walsheim Pro", "Open Sans", sans-serif';

const getTimeAxisLabel = (categories = []) => {
    const sample = categories.find(Boolean);
    if (!sample) return 'Time';
    if (sample.includes('-W')) return 'Time';
    if (sample.length > 7) return 'Time';
    return 'Month';
};

export const GoalProgressChart = ({ chartRef, data = [], events = [], displayMode, goalMastery, totalCount = 0 }) => {
    const [showMastery, setShowMastery] = useState(true);
    const [showTrend, setShowTrend] = useState(true);
    const [showAverage, setShowAverage] = useState(true);
    const [showClientEvents, setShowClientEvents] = useState(true);
    const [showGoalEvents, setShowGoalEvents] = useState(true);
    const hasChartData = Array.isArray(data) && data.length > 0;
    const hasMasteryLine = Boolean(getGoalMasteryLine(goalMastery, true, totalCount));
    const hasMeaningfulData = useMemo(
        () => Array.isArray(data) && data.some((point) => (point?.cumulativeCount || 0) > 0 || (point?.cumulative || 0) > 0),
        [data]
    );

    const option = useMemo(() => {
        if (!data?.length) return null;

        const isPercent = displayMode === displayModeEnums.PERCENT;
        const categories = data.map((point) => point.date);
        const timeAxisLabel = getTimeAxisLabel(categories);
        const seriesData = data.map((point) => (isPercent ? point.cumulative : point.cumulativeCount));
        const yAxisMax = isPercent ? 100 : Math.max(totalCount || 0, ...seriesData, 0, 1);
        const yAxisDisplayMax = isPercent ? 100 : Math.max(Math.ceil(yAxisMax * 1.12), yAxisMax + 1, 1);
        const masteryLine = getGoalMasteryLine(goalMastery, isPercent, data.at(-1)?.totalCount ?? totalCount);
        const eventMarkerY = isPercent ? 96 : Math.max(yAxisMax, yAxisDisplayMax * 0.9);

        const filteredEvents = events.filter((event) => {
            if (event.type === 'Client') return showClientEvents;
            if (event.type === 'Goal' || event.type === 'Target') return showGoalEvents;
            return false;
        });

        const eventBuckets = buildEventBuckets(filteredEvents, categories);
        const eventBucketMap = new Map(eventBuckets.map((bucket) => [bucket.key, bucket]));
        const trendLine = showTrend
            ? buildLinearTrendLine({
                  values: seriesData,
                  maxValue: yAxisDisplayMax,
                  decimals: isPercent ? 1 : 2,
              })
            : { seriesData: [] };
        const averageLine = showAverage
            ? buildAverageLine({
                  values: seriesData,
                  decimals: isPercent ? 1 : 2,
              })
            : { value: null };

        const eventLines = eventBuckets.map((bucket) => ({
            xAxis: bucket.key,
            lineStyle: {
                color: bucket.color,
                type: 'dashed',
                width: 1.5,
            },
            label: { show: false },
        }));

        const eventMarkerSeries = eventBuckets.some((bucket) => bucket.count > 1)
            ? [
                  {
                      name: 'Event Counts',
                      type: 'scatter',
                      silent: true,
                      data: eventBuckets
                          .filter((bucket) => bucket.count > 1)
                          .map((bucket) => ({
                              value: [bucket.key, eventMarkerY],
                              count: bucket.count,
                              itemStyle: { color: bucket.color },
                              label: {
                                  show: true,
                                  formatter: `${bucket.count}`,
                                  color: '#FFFFFF',
                                  fontSize: 11,
                                  fontWeight: 700,
                                  fontFamily: FONT_FAMILY,
                              },
                          })),
                      symbol: 'circle',
                      symbolSize: 18,
                      z: 6,
                  },
              ]
            : [];

        const eventHoverSeries =
            eventBuckets.length > 0
                ? [
                      {
                          name: 'Event Hover',
                          type: 'bar',
                          data: categories.map((category) => (eventBucketMap.has(category) ? yAxisDisplayMax : 0)),
                          barWidth: 12,
                          barGap: '-100%',
                          itemStyle: { color: 'rgba(0, 0, 0, 0)' },
                          emphasis: { itemStyle: { color: 'rgba(0, 0, 0, 0)' } },
                          tooltip: { show: false },
                          z: 1,
                      },
                  ]
                : [];

        return {
            tooltip: {
                trigger: 'axis',
                axisPointer: { type: 'line' },
                extraCssText: `box-shadow:none;background:transparent;border:none;padding:0;font-family:${EVENT_TOOLTIP_FONT_FAMILY};`,
                textStyle: { fontFamily: FONT_FAMILY, fontSize: 12 },
                formatter: (params) => {
                    if (!params?.length) return '';

                    const axisValue = params[0]?.axisValue;
                    const linePoint = params.find((param) => param.seriesName === 'Mastery');
                    const eventTooltip = buildBucketTooltipHtml(eventBucketMap.get(axisValue));
                    const label = formatInsightDate(axisValue, 'MMM YYYY', 'DD MMM YYYY');
                    const value = linePoint?.value ?? 0;
                    const summaryCard = `
                        <div style="padding:12px 8px; background:white; border-radius:8px; box-shadow:0 0 6px 0 rgba(138, 138, 138, 0.24); margin-bottom:${eventTooltip ? '8px' : '0'}; font-family:${EVENT_TOOLTIP_FONT_FAMILY};">
                            <p style="color:#0C66E4; font-size:12px; font-weight:600; line-height:18px; margin:0 0 4px;">${label}</p>
                            <div style="display:flex; align-items:center; gap:6px;">
                                <p style="color:#0C66E4; margin:0;">●</p>
                                <p style="color:#344054; font-size:12px; font-weight:400; line-height:18px; margin:0;">
                                    Mastery:
                                    <span style="color:#344054; font-size:12px; font-weight:600; line-height:18px;"> ${value}${isPercent ? '%' : ''}</span>
                                </p>
                            </div>
                        </div>
                    `;

                    return `${summaryCard}${eventTooltip}`;
                },
            },
            grid: { left: '6%', right: '4%', top: '7%', bottom: '16%', containLabel: true },
            xAxis: {
                type: 'category',
                boundaryGap: false,
                data: categories,
                name: timeAxisLabel,
                nameLocation: 'middle',
                nameGap: 32,
                nameTextStyle: {
                    color: '#667085',
                    fontSize: 12,
                    fontWeight: 600,
                    fontFamily: FONT_FAMILY,
                },
                axisLine: { lineStyle: { color: '#E5E7EB' } },
                axisTick: { show: false },
                axisLabel: {
                    color: '#98A2B3',
                    fontSize: 11,
                    fontFamily: FONT_FAMILY,
                    formatter: (value) => formatInsightDate(value),
                },
            },
            yAxis: {
                type: 'value',
                min: 0,
                max: yAxisDisplayMax,
                name: isPercent ? 'Mastery Rate (%)' : 'Mastered Targets',
                nameLocation: 'middle',
                nameGap: 48,
                nameRotate: 90,
                nameTextStyle: {
                    color: '#667085',
                    fontSize: 12,
                    fontWeight: 600,
                    fontFamily: FONT_FAMILY,
                },
                axisLine: { show: false },
                axisTick: { show: false },
                axisLabel: {
                    color: '#98A2B3',
                    fontSize: 11,
                    fontFamily: FONT_FAMILY,
                    formatter: isPercent ? '{value}%' : '{value}',
                },
                splitLine: { lineStyle: { color: '#F2F4F7' } },
            },
            series: [
                {
                    name: 'Mastery',
                    type: 'line',
                    data: seriesData,
                    symbol: 'circle',
                    showSymbol: true,
                    symbolSize: 6,
                    lineStyle: { color: '#0C66E4', width: 2 },
                    itemStyle: { color: '#0C66E4', borderColor: '#fff', borderWidth: 2 },
                    markLine:
                        eventLines.length > 0
                            ? {
                                  symbol: 'none',
                                  data: eventLines,
                              }
                            : undefined,
                },
                ...(showMastery && masteryLine
                    ? [
                          {
                              name: 'Mastery Threshold',
                              type: 'line',
                              data: [],
                              symbol: 'none',
                              silent: true,
                              markLine: {
                                  symbol: 'none',
                                  silent: true,
                                  data: [
                                      {
                                          yAxis: masteryLine.value,
                                          label: {
                                              show: true,
                                              formatter: masteryLine.label,
                                              position: 'insideEndTop',
                                              fontSize: 10,
                                              color: '#00C48C',
                                              fontFamily: FONT_FAMILY,
                                          },
                                          lineStyle: { color: '#00C48C', type: 'dashed', width: 1.5 },
                                      },
                                  ],
                              },
                              z: 2,
                          },
                      ]
                    : []),
                ...(showAverage && averageLine.value !== null
                    ? [
                          {
                              name: 'Average',
                              type: 'line',
                              data: [],
                              symbol: 'none',
                              silent: true,
                              markLine: {
                                  symbol: 'none',
                                  silent: true,
                                  data: [
                                      {
                                          yAxis: averageLine.value,
                                          label: {
                                              show: true,
                                              formatter: isPercent ? `Avg (${averageLine.value}%)` : `Avg (${averageLine.value})`,
                                              position: 'insideEndBottom',
                                              fontSize: 10,
                                              color: '#7A5AF8',
                                              fontFamily: FONT_FAMILY,
                                          },
                                          lineStyle: { color: '#7A5AF8', type: 'dashed', width: 1.5 },
                                      },
                                  ],
                              },
                              z: 2,
                          },
                      ]
                    : []),
                ...(showTrend && trendLine.seriesData.length > 0
                    ? [
                          {
                              name: 'Trend',
                              type: 'line',
                              data: trendLine.seriesData,
                              symbol: 'none',
                              silent: true,
                              lineStyle: {
                                  color: '#FF6B6B',
                                  width: 2,
                                  opacity: 0.95,
                              },
                              z: 3,
                          },
                      ]
                    : []),
                ...eventHoverSeries,
                ...eventMarkerSeries,
            ],
        };
    }, [data, displayMode, events, goalMastery, showAverage, showClientEvents, showGoalEvents, showMastery, showTrend, totalCount]);

    if (!option) {
        return (
            <>
                <div className="target-progress-chart-shell">
                    <div className="insights-chart-goal-placeholder" />
                    <div className="target-progress-empty-state">
                        <p className="target-progress-empty-title">No targets recorded yet</p>
                        <p className="target-progress-empty-text">
                            There are no registered targets for this goal in the selected date range.
                        </p>
                    </div>
                </div>
                <div className="insights-chart-legend insights-chart-legend-small">
                    <span className="insights-chart-legend-item">
                        <span className="insights-chart-legend-line insights-chart-legend-line-primary" />
                        <span>Mastery</span>
                    </span>
                </div>
            </>
        );
    }

    return (
        <>
            <div className="target-progress-chart-shell">
                <ReactECharts
                    ref={chartRef}
                    option={option}
                    notMerge={true}
                    className="insights-chart-goal"
                    opts={{ renderer: 'canvas' }}
                />
                {hasChartData && !hasMeaningfulData && (
                    <div className="target-progress-empty-state">
                        <p className="target-progress-empty-title">No progress data yet</p>
                        <p className="target-progress-empty-text">
                            No mastery data has been recorded for this goal in the selected date range.
                        </p>
                    </div>
                )}
            </div>
            <div className="insights-chart-legend insights-chart-legend-small">
                <button className={`insights-chart-legend-item active`} onClick={(event) => event.stopPropagation()}>
                    <span className="insights-chart-legend-line insights-chart-legend-line-primary" />
                    <span>Mastery</span>
                </button>
                <button
                    className={`insights-chart-legend-item ${showTrend ? 'active' : ''}`}
                    onClick={(event) => {
                        event.stopPropagation();
                        setShowTrend((prev) => !prev);
                    }}
                >
                    <span className="insights-chart-legend-line insights-chart-legend-line-trend" />
                    <span>Trend</span>
                </button>
                <button
                    className={`insights-chart-legend-item ${showAverage ? 'active' : ''}`}
                    onClick={(event) => {
                        event.stopPropagation();
                        setShowAverage((prev) => !prev);
                    }}
                >
                    <span className="insights-chart-legend-dash insights-chart-legend-dash-average" />
                    <span>Average</span>
                </button>
                {hasMasteryLine && (
                    <button
                        className={`insights-chart-legend-item ${showMastery ? 'active' : ''}`}
                        onClick={(event) => {
                            event.stopPropagation();
                            setShowMastery((prev) => !prev);
                        }}
                    >
                        <span className="insights-chart-legend-dash insights-chart-legend-dash-mastery" />
                        <span>Goal mastery</span>
                    </button>
                )}
                <button
                    className={`insights-chart-legend-item ${showClientEvents ? 'active' : ''}`}
                    onClick={(event) => {
                        event.stopPropagation();
                        setShowClientEvents((prev) => !prev);
                    }}
                >
                    <span className="insights-chart-legend-vertical insights-chart-legend-vertical-client" />
                    <span>Client Events</span>
                </button>
                <button
                    className={`insights-chart-legend-item ${showGoalEvents ? 'active' : ''}`}
                    onClick={(event) => {
                        event.stopPropagation();
                        setShowGoalEvents((prev) => !prev);
                    }}
                >
                    <span className="insights-chart-legend-vertical insights-chart-legend-vertical-goal" />
                    <span>Goal Events</span>
                </button>
            </div>
        </>
    );
};
